import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import * as tenantApi from '../../api/tenantApi';
import { 
    Tenant, 
    TenantCreate, 
    TenantUpdate, 
    StatusChangeRequest,
    MaintenanceTokenResponse 
} from '../../api/tenantApi';

// Types
export interface TenantState {
    tenants: Tenant[];
    deletedTenants: Tenant[];
    selectedTenant: Tenant | null;
    loadingTenant: boolean;
    error: string | null;
    maintenanceToken: string | null;
}

// Initial state
const initialState: TenantState = {
    tenants: [],
    deletedTenants: [],
    selectedTenant: null,
    loadingTenant: false,
    error: null,
    maintenanceToken: null
};

export const testTenantProvider = createAsyncThunk(
    'tenants/testProvider',
    async ({ tenantDomain }: { tenantDomain: string }, { rejectWithValue }) => {
        try {
            const result = await tenantApi.testTenantProvider(tenantDomain);
            return result;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to test tenant provider');
        }
    }
);

// Async thunks
export const fetchTenants = createAsyncThunk(
    'tenants/fetchAll',
    async (_, { rejectWithValue }) => {
        try {
            const response = await tenantApi.getAllTenants();
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch tenants');
        }
    }
);

export const fetchDeletedTenants = createAsyncThunk(
    'tenants/fetchDeleted',
    async (_, { rejectWithValue }) => {
        try {
            const response = await tenantApi.getDeletedTenants();
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch deleted tenants');
        }
    }
);

export const fetchTenantById = createAsyncThunk(
    'tenants/fetchById',
    async (id: string, { rejectWithValue }) => {
        try {
            const response = await tenantApi.getTenantById(id);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch tenant');
        }
    }
);

export const createTenant = createAsyncThunk(
    'tenants/create',
    async (tenantData: TenantCreate, { rejectWithValue }) => {
        try {
            const response = await tenantApi.createTenant(tenantData);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to create tenant');
        }
    }
);

export const updateTenant = createAsyncThunk(
    'tenants/update',
    async ({ id, updateData }: { id: string; updateData: TenantUpdate }, { rejectWithValue }) => {
        try {
            const response = await tenantApi.updateTenant(id, updateData);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to update tenant');
        }
    }
);

export const changeTenantStatus = createAsyncThunk(
    'tenants/changeStatus',
    async ({ 
        id, 
        statusData,
        includeToken = false 
    }: { 
        id: string; 
        statusData: StatusChangeRequest;
        includeToken?: boolean;
    }, { rejectWithValue }) => {
        try {
            const response = await tenantApi.changeTenantStatus(id, statusData, includeToken);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to change tenant status');
        }
    }
);

export const getMaintenanceToken = createAsyncThunk(
    'tenants/getMaintenanceToken',
    async (id: string, { rejectWithValue }) => {
        try {
            const response = await tenantApi.getMaintenanceToken(id);
            return response.data.token;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to get maintenance token');
        }
    }
);

export const deleteTenant = createAsyncThunk(
    'tenants/delete',
    async (id: string, { rejectWithValue }) => {
        try {
            await tenantApi.deleteTenant(id);
            return id;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to delete tenant');
        }
    }
);

export const restoreTenant = createAsyncThunk(
    'tenants/restore',
    async (id: string, { rejectWithValue }) => {
        try {
            const response = await tenantApi.restoreTenant(id);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to restore tenant');
        }
    }
);

export const permanentlyDeleteTenant = createAsyncThunk(
    'tenants/permanentlyDelete',
    async (id: string, { rejectWithValue }) => {
        try {
            await tenantApi.permanentlyDeleteTenant(id);
            return id;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to permanently delete tenant');
        }
    }
);

export const duplicateTenant = createAsyncThunk(
    'tenants/duplicate',
    async (
        { id, newData }: { id: string; newData: Partial<TenantCreate> },
        { rejectWithValue }
    ) => {
        try {
            const response = await tenantApi.duplicateTenant(id, newData);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to duplicate tenant');
        }
    }
);

// Slice
const tenantSlice = createSlice({
    name: 'tenants',
    initialState,
    reducers: {
        clearSelectedTenant: (state) => {
            state.selectedTenant = null;
        },
        clearTenantError: (state) => {
            state.error = null;
        },
        clearMaintenanceToken: (state) => {
            state.maintenanceToken = null;
        }
    },
    extraReducers: (builder) => {
        // Fetch all tenants
        builder.addCase(fetchTenants.pending, (state) => {
            state.loadingTenant = true;
            state.error = null;
        });
        builder.addCase(fetchTenants.fulfilled, (state, action: PayloadAction<Tenant[]>) => {
            state.loadingTenant = false;
            state.tenants = action.payload;
        });
        builder.addCase(fetchTenants.rejected, (state, action) => {
            state.loadingTenant = false;
            state.error = action.payload as string;
        });

        // Fetch deleted tenants
        builder.addCase(fetchDeletedTenants.pending, (state) => {
            state.loadingTenant = true;
            state.error = null;
        });
        builder.addCase(fetchDeletedTenants.fulfilled, (state, action: PayloadAction<Tenant[]>) => {
            state.loadingTenant = false;
            state.deletedTenants = action.payload;
        });
        builder.addCase(fetchDeletedTenants.rejected, (state, action) => {
            state.loadingTenant = false;
            state.error = action.payload as string;
        });

        // Fetch tenant by ID
        builder.addCase(fetchTenantById.pending, (state) => {
            state.loadingTenant = true;
            state.error = null;
        });
        builder.addCase(fetchTenantById.fulfilled, (state, action: PayloadAction<Tenant>) => {
            state.loadingTenant = false;
            state.selectedTenant = action.payload;
        });
        builder.addCase(fetchTenantById.rejected, (state, action) => {
            state.loadingTenant = false;
            state.error = action.payload as string;
        });

        // Create tenant
        builder.addCase(createTenant.pending, (state) => {
            state.loadingTenant = true;
            state.error = null;
        });
        builder.addCase(createTenant.fulfilled, (state, action: PayloadAction<Tenant>) => {
            state.loadingTenant = false;
            state.tenants.push(action.payload);
            state.selectedTenant = action.payload;
        });
        builder.addCase(createTenant.rejected, (state, action) => {
            state.loadingTenant = false;
            state.error = action.payload as string;
        });

        // Update tenant
        builder.addCase(updateTenant.pending, (state) => {
            state.loadingTenant = true;
            state.error = null;
        });
        builder.addCase(updateTenant.fulfilled, (state, action: PayloadAction<Tenant>) => {
            state.loadingTenant = false;

            // Update in the list
            const index = state.tenants.findIndex((t) => t._id === action.payload._id);
            if (index !== -1) {
                state.tenants[index] = action.payload;
            }

            // Update selected tenant if it's the same one
            if (state.selectedTenant && state.selectedTenant._id === action.payload._id) {
                state.selectedTenant = action.payload;
            }
        });
        builder.addCase(updateTenant.rejected, (state, action) => {
            state.loadingTenant = false;
            state.error = action.payload as string;
        });

        // Change tenant status
        builder.addCase(changeTenantStatus.pending, (state) => {
            state.loadingTenant = true;
            state.error = null;
        });
        builder.addCase(changeTenantStatus.fulfilled, (state, action: PayloadAction<Tenant>) => {
            state.loadingTenant = false;
            
            // Update in the list
            const index = state.tenants.findIndex((t) => t._id === action.payload._id);
            if (index !== -1) {
                state.tenants[index] = action.payload;
            }

            // Update selected tenant if it's the same one
            if (state.selectedTenant && state.selectedTenant._id === action.payload._id) {
                state.selectedTenant = action.payload;
            }

            // Store maintenance token if available
            if (action.payload.maintenanceToken) {
                state.maintenanceToken = action.payload.maintenanceToken;
            }
        });
        builder.addCase(changeTenantStatus.rejected, (state, action) => {
            state.loadingTenant = false;
            state.error = action.payload as string;
        });

        // Get maintenance token
        builder.addCase(getMaintenanceToken.pending, (state) => {
            state.loadingTenant = true;
            state.error = null;
        });
        builder.addCase(getMaintenanceToken.fulfilled, (state, action: PayloadAction<string>) => {
            state.loadingTenant = false;
            state.maintenanceToken = action.payload;
        });
        builder.addCase(getMaintenanceToken.rejected, (state, action) => {
            state.loadingTenant = false;
            state.error = action.payload as string;
        });

        // Delete tenant
        builder.addCase(deleteTenant.pending, (state) => {
            state.loadingTenant = true;
            state.error = null;
        });
        builder.addCase(deleteTenant.fulfilled, (state, action: PayloadAction<string>) => {
            state.loadingTenant = false;
            state.tenants = state.tenants.filter((tenant) => tenant._id !== action.payload);

            if (state.selectedTenant && state.selectedTenant._id === action.payload) {
                state.selectedTenant = null;
            }
        });
        builder.addCase(deleteTenant.rejected, (state, action) => {
            state.loadingTenant = false;
            state.error = action.payload as string;
        });

        // Restore tenant
        builder.addCase(restoreTenant.pending, (state) => {
            state.loadingTenant = true;
            state.error = null;
        });
        builder.addCase(restoreTenant.fulfilled, (state, action: PayloadAction<Tenant>) => {
            state.loadingTenant = false;
            state.tenants.push(action.payload);
            state.deletedTenants = state.deletedTenants.filter(
                (tenant) => tenant._id !== action.payload._id
            );
        });
        builder.addCase(restoreTenant.rejected, (state, action) => {
            state.loadingTenant = false;
            state.error = action.payload as string;
        });

        // Permanently delete tenant
        builder.addCase(permanentlyDeleteTenant.pending, (state) => {
            state.loadingTenant = true;
            state.error = null;
        });
        builder.addCase(permanentlyDeleteTenant.fulfilled, (state, action: PayloadAction<string>) => {
            state.loadingTenant = false;
            state.deletedTenants = state.deletedTenants.filter(
                (tenant) => tenant._id !== action.payload
            );
        });
        builder.addCase(permanentlyDeleteTenant.rejected, (state, action) => {
            state.loadingTenant = false;
            state.error = action.payload as string;
        });

        // Duplicate tenant
        builder.addCase(duplicateTenant.pending, (state) => {
            state.loadingTenant = true;
            state.error = null;
        });
        builder.addCase(duplicateTenant.fulfilled, (state, action: PayloadAction<Tenant>) => {
            state.loadingTenant = false;
            state.tenants.push(action.payload);
        });
        builder.addCase(duplicateTenant.rejected, (state, action) => {
            state.loadingTenant = false;
            state.error = action.payload as string;
        });
    },
});

export const { clearSelectedTenant, clearTenantError, clearMaintenanceToken } = tenantSlice.actions;

export default tenantSlice.reducer;